/**
 * Riders Corner Presenter Component
 *
 * Presentational component for riders corner page
 * Follows Single Responsibility Principle: Only handles UI rendering
 * Open/Closed Principle: Can be extended with additional props without modifying existing code
 */

import React from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { ThemeProvider } from '@/design-system/themes';
import { Sidebar } from '@/components/navbar/web/Sidebar';
import { MobileNavbar } from '@/components/navbar/mobile/MobileNavbar';
import {
  RidersCornerHeader,
  RidersCornerSearch,
  RidersCornerCard,
} from '@/components/riders-corner';
import { Footer } from '@/components/common/Footer';
import { RidersCornerListItem } from '@/services/ridersCornerService';
import { spacing, backgroundColors } from '@/design-system/utils';

export interface RidersCornerPresenterProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onSearchSubmit: (value: string) => void;
  riders: RidersCornerListItem[];
  isLoading: boolean;
  error: string | null;
  hasSearchQuery: boolean;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  onJoinCommunity?: (email: string) => void;
  onNewChat?: () => void;
  onLogoClick?: () => void;
}

export const RidersCornerPresenter: React.FC<RidersCornerPresenterProps> = ({
  searchQuery,
  onSearchChange,
  onSearchSubmit,
  riders,
  isLoading,
  error,
  hasSearchQuery,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  onJoinCommunity,
  onNewChat,
  onLogoClick,
}) => {
  return (
    <ThemeProvider defaultMode='light'>
      <div className='landing-page-v2-container min-h-screen bg-white overflow-x-hidden'>
        {/* Mobile Navigation - Visible only on mobile */}
        <MobileNavbar onNewChat={onNewChat} onLogoClick={onLogoClick} />

        {/* Sidebar Navigation - Hidden on mobile */}
        <Sidebar onNewChat={onNewChat} onLogoClick={onLogoClick} />

        {/* Main Content */}
        <main
          className='ml-0 md:ml-[106px] pt-[56px] md:pt-0 overflow-x-hidden'
          style={{ backgroundColor: backgroundColors.primary }}
        >
          {/* Content Container - Matching Figma specs */}
          <div
            className='mx-auto w-full flex flex-col justify-center items-center px-4 md:px-24'
            style={{
              maxWidth: '1343px',
              paddingTop: spacing[14], // 56px
              paddingBottom: spacing[14], // 56px
              gap: spacing[12], // 48px gap
            }}
          >
            {/* Header Section */}
            <RidersCornerHeader />

            {/* Search Bar */}
            <div className='flex justify-center w-full'>
              <RidersCornerSearch
                value={searchQuery}
                onChange={onSearchChange}
                onSubmit={onSearchSubmit}
              />
            </div>

            {/* Trip Cards Grid */}
            <div className='w-full'>
              {error && (
                <div className='text-center text-red-500 mb-4'>{error}</div>
              )}

              {/* Loading Skeletons */}
              {isLoading && (
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-[18px]'>
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div
                      key={index}
                      className='flex flex-col md:flex-row w-full bg-white rounded-lg overflow-hidden'
                      style={{
                        borderRadius: '12px',
                        border: '1px solid #e7e5e4',
                        padding: '12px 16px 12px 12px',
                        gap: '16px',
                      }}
                    >
                      {/* Image Skeleton with Badge Skeleton */}
                      <div
                        className='relative w-full rounded-[8px] overflow-hidden flex-shrink-0'
                        style={{
                          width: '100%',
                          maxWidth: '213px',
                          height: '220px',
                          minHeight: '220px',
                          alignSelf: 'stretch',
                        }}
                      >
                        <Skeleton className='h-full w-full rounded-[8px]' />
                        {/* Badge skeleton on top right */}
                        <div className='absolute top-2 right-2'>
                          <Skeleton height={21} width={80} borderRadius={4} />
                        </div>
                      </div>
                      {/* Content Skeleton */}
                      <div
                        className='flex-1 flex flex-col gap-4'
                        style={{ paddingTop: spacing[4] }}
                      >
                        <div className='flex flex-col gap-2'>
                          <Skeleton height={28} width='100%' />
                          <Skeleton height={20} width='90%' />
                          <Skeleton height={20} width='75%' />
                        </div>
                        <div className='flex items-center gap-2'>
                          <Skeleton height={28} width={100} borderRadius={6} />
                          <Skeleton height={28} width={120} borderRadius={6} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Riders List */}
              {!isLoading && riders.length > 0 && (
                <>
                  <div
                    className='grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-[18px] w-full'
                    style={{
                      gap: '18px', // 18px gap between cards per Figma
                    }}
                  >
                    {riders.map(rider => (
                      <RidersCornerCard key={rider._id} item={rider} />
                    ))}
                  </div>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className='flex justify-center items-center mt-8 gap-2'>
                      {/* Previous Button */}
                      <button
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className='px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                        style={{
                          fontFamily: 'Gantari, sans-serif',
                          fontSize: '14px',
                          fontWeight: 500,
                        }}
                      >
                        Previous
                      </button>

                      {/* Page Numbers */}
                      <div className='flex gap-1'>
                        {Array.from(
                          { length: totalPages },
                          (_, i) => i + 1
                        ).map(pageNum => (
                          <button
                            key={pageNum}
                            onClick={() => onPageChange(pageNum)}
                            className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                              currentPage === pageNum
                                ? 'bg-[#F8EC26] text-black'
                                : 'bg-white border border-gray-300 hover:bg-gray-50 text-gray-700'
                            }`}
                            style={{
                              fontFamily: 'Gantari, sans-serif',
                              fontSize: '14px',
                              fontWeight: currentPage === pageNum ? 600 : 500,
                            }}
                          >
                            {pageNum}
                          </button>
                        ))}
                      </div>

                      {/* Next Button */}
                      <button
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className='px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                        style={{
                          fontFamily: 'Gantari, sans-serif',
                          fontSize: '14px',
                          fontWeight: 500,
                        }}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* Empty State */}
              {!isLoading && riders.length === 0 && (
                <div className='text-center py-12'>
                  <p className='text-lg' style={{ color: '#44403c' }}>
                    {hasSearchQuery ? 'No results found' : 'No results found'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Footer with Newsletter Signup */}
          <Footer onJoinCommunity={onJoinCommunity} />
        </main>
      </div>
    </ThemeProvider>
  );
};
